import { useMemo } from 'react';

import { useCollectionStore } from '@stores/useCollectionStore';

import { buildHexagonFilterBody } from '../utils/buildHexagonFilterBody';

/**
 * The per-object filter body every collection request is built from, plus the
 * one predicate its callers must not forget.
 *
 * Three surfaces post this identical map — the hexagon aggregation, the DSP
 * aggregation, and the two server-side exports — and each was rebuilding it
 * from the same three inputs. Deriving it once here keeps them from drifting
 * apart, and means a change to the builder's arguments is made in one place.
 *
 * `hasVisibleObjects` travels with the body because it is not a property of the
 * body alone: `buildHexagonFilterBody` returns `{}` for "no object is visible",
 * which every endpoint reads as *aggregate/export everything* — the opposite of
 * what the screen shows. Its module docblock states that caller contract; this
 * hook is what saves each caller from hand-writing the check.
 *
 * `checkedNodes` is read from the store rather than taken as an argument: it is
 * the same slice for every caller, and passing it in only invited a fourth
 * subscription that could be wired to the wrong selector.
 *
 * @param {object} savedCollectionFilters - persisted collection-level filters
 * @param {Array<object>} objects         - collection objects, each with savedFilters
 * @returns {{ filterBody: object, hasVisibleObjects: boolean }}
 */
const useCollectionFilterBody = (savedCollectionFilters, objects) => {
	const checkedNodes = useCollectionStore(s => s.checkedNodes);

	return useMemo(() => {
		const filterBody = buildHexagonFilterBody(savedCollectionFilters, objects, checkedNodes);
		return { filterBody, hasVisibleObjects: Object.keys(filterBody).length > 0 };
	}, [savedCollectionFilters, objects, checkedNodes]);
};

export default useCollectionFilterBody;
